// Every call the web app makes to apps/api, through the typed `hc<AppType>`
// client. Response types are derived from the routes with InferResponseType
//: nothing here hand-writes a response shape.
import type { InferRequestType, InferResponseType } from "hono/client";
import { API_URL, api } from "./client";

// ---------------------------------------------------------------------------
// Types derived from the API routes (docs/contract.md section 5).
// ---------------------------------------------------------------------------
type MeRes = InferResponseType<typeof api.auth.me.$get, 200>;
type CatalogRes = InferResponseType<typeof api.catalog.$get, 200>;
type DocumentsRes = InferResponseType<typeof api.documents.$get, 200>;
type DocumentRes = InferResponseType<(typeof api.documents)[":id"]["$get"], 200>;
type GenerateRes = InferResponseType<typeof api.documents.generate.$post, 201>;
type VersionRes = InferResponseType<(typeof api.documents)[":id"]["versions"][":version"]["$get"], 200>;
type PatchDocRes = InferResponseType<(typeof api.documents)[":id"]["$patch"], 200>;
type RegenerateRes = InferResponseType<(typeof api.documents)[":id"]["regenerate"]["$post"], 200>;
type TransitionRes = InferResponseType<(typeof api.documents)[":id"]["transition"]["$post"], 200>;
type DashboardRes = InferResponseType<typeof api.dashboard.$get, 200>;
type KnowledgeRes = InferResponseType<typeof api.knowledge.$get, 200>;
type UsersRes = InferResponseType<typeof api.users.$get, 200>;
type UserPatchRes = InferResponseType<(typeof api.users)[":id"]["$patch"], 200>;
type LoginRes = InferResponseType<typeof api.auth.login.$post, 200>;
// Asisten AI (docs/contract.md section 8).
type FaqRes = InferResponseType<typeof api.chat.faq.$get, 200>;
type ConversationsRes = InferResponseType<typeof api.chat.conversations.$get, 200>;
type ConversationCreateRes = InferResponseType<typeof api.chat.conversations.$post, 201>;
type ConversationRes = InferResponseType<(typeof api.chat.conversations)[":id"]["$get"], 200>;
type ChatTurnRes = InferResponseType<(typeof api.chat.conversations)[":id"]["messages"]["$post"], 200>;
type ConversationPatchRes = InferResponseType<(typeof api.chat.conversations)[":id"]["$patch"], 200>;

export type User = MeRes["user"];
export type Role = User["role"];
export type CatalogTahap = CatalogRes["tahap"][number];
export type CatalogDocType = CatalogTahap["documentTypes"][number];
export type CatalogField = CatalogDocType["fields"][number];
export type Tahap = CatalogTahap["id"];
export type DocumentSummary = DocumentsRes["documents"][number];
export type DocumentDetail = DocumentRes["document"];
export type DocStatus = DocumentSummary["status"];
export type DocumentEvent = DocumentDetail["events"][number];
export type DocumentVersion = DocumentDetail["versions"][number];
export type VersionWithContent = VersionRes["version"];
export type Dashboard = DashboardRes;
export type Knowledge = KnowledgeRes;
export type KnowledgeFile = KnowledgeRes["files"][number];

export type FaqItem = FaqRes["items"][number];
export type ConversationSummary = ConversationsRes["conversations"][number];
export type ConversationDetail = ConversationRes["conversation"];
export type ChatMessage = ConversationDetail["messages"][number];

export type GenerateInput = InferRequestType<typeof api.documents.generate.$post>["json"];
export type UserPatchInput = InferRequestType<(typeof api.users)[":id"]["$patch"]>["json"];
export type ChatTurnInput = InferRequestType<typeof api.chat.conversations.$post>["json"];
export type DocumentsQuery = InferRequestType<typeof api.documents.$get>["query"];
export type UsersQuery = InferRequestType<typeof api.users.$get>["query"];
export type TransitionAction = InferRequestType<(typeof api.documents)[":id"]["transition"]["$post"]>["json"]["action"];

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------
export class ApiError extends Error {
  readonly status: number;
  /** A first chat turn that failed at the AI still stored its conversation (contract 8.5). */
  readonly conversationId: string | null;
  /** Seconds from the Retry-After header of a 429, when the API sent one. */
  readonly retryAfter: number | null;
  /** The parsed JSON error body, when there was one (field-level details of a 422). */
  readonly body: Record<string, unknown> | null;
  constructor(status: number, message: string, conversationId: string | null = null, retryAfter: number | null = null, body: Record<string, unknown> | null = null) {
    super(message);
    this.status = status;
    this.conversationId = conversationId;
    this.retryAfter = retryAfter;
    this.body = body;
    this.name = "ApiError";
  }
}

const FALLBACK: Record<number, string> = {
  0: "Tidak dapat terhubung ke server. Periksa koneksi lalu coba lagi.",
  400: "Data yang dikirim belum valid.",
  401: "Sesi Anda berakhir. Silakan masuk kembali.",
  403: "Anda tidak memiliki akses untuk tindakan ini.",
  404: "Data tidak ditemukan.",
  409: "Tindakan ini bentrok dengan data yang ada.",
  422: "Data belum lengkap atau tidak sesuai.",
  429: "Terlalu banyak percobaan. Coba lagi nanti.",
  502: "Pluto sedang sibuk, coba lagi sebentar.",
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return FALLBACK[0];
}

type Unauthorized = () => void;
let onUnauthorized: Unauthorized | null = null;

/** The auth provider registers this so any 401 returns the app to Masuk. */
export function setUnauthorizedHandler(fn: Unauthorized | null): void {
  onUnauthorized = fn;
}

type AgreementRequired = () => void;
let onAgreementRequired: AgreementRequired | null = null;

/**
 * The auth provider registers this so any 403 { code: "agreement_required" }
 * (a newer User Agreement waits) sends the user to the agreement step.
 */
export function setAgreementRequiredHandler(fn: AgreementRequired | null): void {
  onAgreementRequired = fn;
}

/** True for the API's 403 that asks for the current User Agreement first. */
export function isAgreementRequired(status: number, body: unknown): boolean {
  return status === 403 && !!body && typeof body === "object" && (body as { code?: unknown }).code === "agreement_required";
}

let onAccountDisabled: (() => void) | null = null;

/** The auth provider registers this so any 403 { code: "account_disabled" } shows the disabled screen. */
export function setAccountDisabledHandler(fn: (() => void) | null): void {
  onAccountDisabled = fn;
}

/** True for the 403 every route answers once the admin has deactivated the account. */
export function isAccountDisabled(status: number, body: unknown): boolean {
  return status === 403 && !!body && typeof body === "object" && (body as { code?: unknown }).code === "account_disabled";
}

/** Reports a 403 agreement_required seen outside `send` (chat/documents.ts). */
export function notifyAgreementRequired(): void {
  onAgreementRequired?.();
}

interface ResLike {
  ok: boolean;
  status: number;
  headers?: { get(name: string): string | null };
  json(): Promise<unknown>;
  text(): Promise<string>;
}

/** Retry-After as seconds (the delta form or an HTTP date), or null. */
export function parseRetryAfter(value: string | null | undefined, now: number = Date.now()): number | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.round((at - now) / 1000));
}

async function failure(res: ResLike, notify401: boolean): Promise<never> {
  let message = FALLBACK[res.status] ?? "Terjadi kesalahan. Coba lagi.";
  let conversationId: string | null = null;
  let parsed: Record<string, unknown> | null = null;
  try {
    const body = (await res.json()) as { error?: unknown; conversationId?: unknown } | null;
    if (body && typeof body.error === "string" && body.error.trim()) message = body.error;
    if (body && typeof body.conversationId === "string" && body.conversationId) conversationId = body.conversationId;
    if (body && typeof body === "object") parsed = body as Record<string, unknown>;
  } catch {
    // Not JSON: keep the fallback text for this status.
  }
  if (res.status === 401 && notify401) onUnauthorized?.();
  if (isAgreementRequired(res.status, parsed)) onAgreementRequired?.();
  if (isAccountDisabled(res.status, parsed)) onAccountDisabled?.();
  const retryAfter = res.status === 429 ? parseRetryAfter(res.headers?.get("Retry-After")) : null;
  throw new ApiError(res.status, message, conversationId, retryAfter, parsed);
}

/** One typed-client call: its JSON body, or an ApiError (exported for the feature clients, e.g. project/work/api.ts). */
export async function send<T>(request: () => Promise<ResLike>, notify401 = true): Promise<T> {
  let res: ResLike;
  try {
    res = await request();
  } catch {
    throw new ApiError(0, FALLBACK[0]);
  }
  if (!res.ok) return failure(res, notify401);
  return (await res.json()) as T;
}

/** Drops empty filter values so the query only carries what the user set. */
function clean<T extends Record<string, unknown>>(q: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== "") out[k] = v;
  return out as T;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export async function login(email: string, password: string): Promise<User> {
  const body = await send<LoginRes>(() => api.auth.login.$post({ json: { email, password } }), false);
  return body.user;
}

export async function logout(): Promise<void> {
  await send(() => api.auth.logout.$post(), false);
}

/** The signed-in session: the user and whether the current User Agreement still waits (non-admin only). */
export interface Session {
  user: User;
  agreementRequired: boolean;
}

export async function me(): Promise<Session> {
  const body = await send<MeRes>(() => api.auth.me.$get(), false);
  return { user: body.user, agreementRequired: body.agreementRequired === true };
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await send(() => api.auth.password.$post({ json: { currentPassword, newPassword } }));
}

// ---------------------------------------------------------------------------
// Users (admin)
// ---------------------------------------------------------------------------
export type AccountRow = UsersRes["users"][number];
export type AccountStatus = User["status"];
type StatusHistoryRes = InferResponseType<(typeof api.users)[":id"]["status-history"]["$get"], 200>;
export type StatusEvent = StatusHistoryRes["events"][number];

export async function listUsers(query: UsersQuery): Promise<AccountRow[]> {
  const body = await send<UsersRes>(() => api.users.$get({ query: clean(query) }));
  return body.users;
}

export async function updateUser(id: string, patch: UserPatchInput): Promise<User> {
  const body = await send<UserPatchRes>(() => api.users[":id"].$patch({ param: { id }, json: patch }));
  return body.user;
}

/** PATCH /users/:id/status: the admin activates (after payment) or deactivates an account. */
export async function setUserStatus(id: string, status: "aktif" | "nonaktif"): Promise<User> {
  const body = await send<UserPatchRes>(() => api.users[":id"].status.$patch({ param: { id }, json: { status } }));
  return body.user;
}

export async function getStatusHistory(id: string): Promise<StatusEvent[]> {
  const body = await send<StatusHistoryRes>(() => api.users[":id"]["status-history"].$get({ param: { id } }));
  return body.events;
}

/** DELETE /users/:id (contract 9.1): 409 while the user owns documents, 422 for self. */
export async function deleteUser(id: string): Promise<void> {
  await send(() => section9.users[":id"].$delete({ param: { id } }));
}

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------
export async function getCatalog(): Promise<CatalogTahap[]> {
  const body = await send<CatalogRes>(() => api.catalog.$get());
  return body.tahap;
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------
export async function generateDocument(input: GenerateInput): Promise<DocumentDetail> {
  const body = await send<GenerateRes>(() => api.documents.generate.$post({ json: input }));
  return body.document;
}

export async function listDocuments(query: DocumentsQuery): Promise<DocumentSummary[]> {
  const body = await send<DocumentsRes>(() => api.documents.$get({ query: clean(query) }));
  return body.documents;
}

export async function getDocument(id: string): Promise<DocumentDetail> {
  const body = await send<DocumentRes>(() => api.documents[":id"].$get({ param: { id } }));
  return body.document;
}

export async function getVersion(id: string, version: number): Promise<VersionWithContent> {
  const body = await send<VersionRes>(() =>
    api.documents[":id"].versions[":version"].$get({ param: { id, version: String(version) } }),
  );
  return body.version;
}

export async function saveDocument(id: string, content: string, title?: string): Promise<DocumentDetail> {
  const body = await send<PatchDocRes>(() => api.documents[":id"].$patch({ param: { id }, json: clean({ title, content }) }));
  return body.document;
}

export async function regenerateDocument(id: string, instructions?: string): Promise<DocumentDetail> {
  const body = await send<RegenerateRes>(() =>
    api.documents[":id"].regenerate.$post({ param: { id }, json: clean({ instructions }) }),
  );
  return body.document;
}

export async function transitionDocument(id: string, action: TransitionAction, note?: string): Promise<DocumentDetail> {
  const body = await send<TransitionRes>(() =>
    api.documents[":id"].transition.$post({ param: { id }, json: clean({ action, note }) }),
  );
  return body.document;
}

/** The Markdown export as text (GET /documents/:id/export?format=md). */
export async function exportDocument(id: string): Promise<string> {
  let res: ResLike;
  try {
    res = await api.documents[":id"].export.$get({ param: { id }, query: { format: "md" } });
  } catch {
    throw new ApiError(0, FALLBACK[0]);
  }
  if (!res.ok) return failure(res, true);
  return res.text();
}

// ---------------------------------------------------------------------------
// Dashboard, knowledge
// ---------------------------------------------------------------------------
export async function getDashboard(): Promise<Dashboard> {
  return send<DashboardRes>(() => api.dashboard.$get());
}

export async function getKnowledge(): Promise<Knowledge> {
  return send<KnowledgeRes>(() => api.knowledge.$get());
}

export async function reloadKnowledge(): Promise<Knowledge> {
  return send<KnowledgeRes>(() => api.knowledge.reload.$post());
}

// ---------------------------------------------------------------------------
// Admin console, Basis Pengetahuan upload (docs/contract.md section 9.2)
//
// The section 9 routes are built in parallel in apps/api. Until AppType
// carries them, these shapes are the contract's, written once here and read
// through a typed view of the same hc client (the hc proxy builds any path at
// runtime). When AppType gains the routes, replace each type below with its
// InferResponseType and drop `section9`.
// ---------------------------------------------------------------------------
export type SystemFileKind = "catalog" | "faq" | "elearning";

export interface SystemFile {
  file: string;
  kind: SystemFileKind;
  itemCount: number;
  updatedAt: string | null;
}

/** GET /knowledge as an admin sees it: updatedAt and size per file, plus the system files. */
export type AdminKnowledgeFile = KnowledgeFile & { updatedAt?: string | null; size?: number | null };
export type AdminKnowledge = Omit<Knowledge, "files"> & { files: AdminKnowledgeFile[]; system?: SystemFile[] };

export interface KnowledgeRaw {
  file: string;
  content: string;
}

export interface KnowledgeWriteResult {
  file: string;
  entry: unknown;
}

/** The validator's verdict: `summary` is shown as given (text, or the fields it names). */
export interface KnowledgeValidation {
  ok: true;
  summary: unknown;
}

interface Section9Client {
  users: { ":id": { $delete(args: { param: { id: string } }): Promise<ResLike> } };
  knowledge: {
    files: {
      $post(args: { json: { file: string; content: string } }): Promise<ResLike>;
      ":file": {
        $get(args: { param: { file: string } }): Promise<ResLike>;
        $put(args: { param: { file: string }; json: { content: string } }): Promise<ResLike>;
        $delete(args: { param: { file: string } }): Promise<ResLike>;
      };
    };
    validate: { $post(args: { json: { file: string; content: string } }): Promise<ResLike> };
  };
}

const section9 = api as unknown as Section9Client;

export async function getAdminKnowledge(): Promise<AdminKnowledge> {
  return send<AdminKnowledge>(() => api.knowledge.$get());
}

export async function reloadAdminKnowledge(): Promise<AdminKnowledge> {
  return send<AdminKnowledge>(() => api.knowledge.reload.$post());
}

export async function getKnowledgeFile(file: string): Promise<KnowledgeRaw> {
  return send<KnowledgeRaw>(() => section9.knowledge.files[":file"].$get({ param: { file } }));
}

/** POST /knowledge/validate: resolves with the summary, rejects with the 422 line/column message. */
export async function validateKnowledge(file: string, content: string): Promise<KnowledgeValidation> {
  return send<KnowledgeValidation>(() => section9.knowledge.validate.$post({ json: { file, content } }));
}

export async function createKnowledgeFile(file: string, content: string): Promise<KnowledgeWriteResult> {
  return send<KnowledgeWriteResult>(() => section9.knowledge.files.$post({ json: { file, content } }));
}

export async function replaceKnowledgeFile(file: string, content: string): Promise<KnowledgeWriteResult> {
  return send<KnowledgeWriteResult>(() => section9.knowledge.files[":file"].$put({ param: { file }, json: { content } }));
}

export async function deleteKnowledgeFile(file: string): Promise<void> {
  await send(() => section9.knowledge.files[":file"].$delete({ param: { file } }));
}

// ---------------------------------------------------------------------------
// Asisten AI: FAQ and private conversations
// ---------------------------------------------------------------------------
export async function listFaq(): Promise<FaqItem[]> {
  const body = await send<FaqRes>(() => api.chat.faq.$get());
  return body.items;
}

export async function listConversations(q?: string): Promise<ConversationSummary[]> {
  const body = await send<ConversationsRes>(() => api.chat.conversations.$get({ query: clean({ q }) }));
  return body.conversations;
}

/** Cari chat: every conversation of the user, project conversations included (with projectName). */
export async function searchConversations(q?: string): Promise<ConversationSummary[]> {
  const body = await send<ConversationsRes>(() => api.chat.conversations.$get({ query: clean({ q, scope: "all" as const }) }));
  return body.conversations;
}

/** Creates the conversation and answers its first turn. */
export async function createConversation(input: ChatTurnInput): Promise<ConversationDetail> {
  const body = await send<ConversationCreateRes>(() => api.chat.conversations.$post({ json: input }));
  return body.conversation;
}

export async function getConversation(id: string): Promise<ConversationDetail> {
  const body = await send<ConversationRes>(() => api.chat.conversations[":id"].$get({ param: { id } }));
  return body.conversation;
}

/** The stored user message plus the assistant reply. */
export async function sendTurn(id: string, input: ChatTurnInput): Promise<ChatMessage[]> {
  const body = await send<ChatTurnRes>(() => api.chat.conversations[":id"].messages.$post({ param: { id }, json: input }));
  return body.messages;
}

export async function renameConversation(id: string, title: string): Promise<ConversationSummary> {
  const body = await send<ConversationPatchRes>(() => api.chat.conversations[":id"].$patch({ param: { id }, json: { title } }));
  return body.conversation;
}

export async function deleteConversation(id: string): Promise<void> {
  await send(() => api.chat.conversations[":id"].$delete({ param: { id } }));
}

/** Clears the conversation's memory: the AI forgets the conversation so far (contract 8.6). */
export async function clearConversationMemory(id: string): Promise<void> {
  await send(() => api.chat.conversations[":id"].memory.$delete({ param: { id } }));
}

// ---------------------------------------------------------------------------
// Database browser (admin, read-only)
// ---------------------------------------------------------------------------
export interface DbColumnReference {
  table: string;
  column: string;
  /** The column read for the human-readable name, e.g. `name` or `title`. */
  display: string;
  /** One hop further, when the target row itself belongs to someone. */
  via?: { foreignKey: string; table: string; column: string; display: string };
}

export interface DbRowLabel {
  /** The target row's display value, e.g. a conversation title. */
  name: string;
  /** The owner one hop further, e.g. the user who owns that conversation. */
  extra?: string;
}

export interface DbColumn {
  name: string;
  dataType: string;
  nullable: boolean;
  filterable: boolean;
  /** Present when the column is a foreign key that can be shown as a name. */
  references?: DbColumnReference;
}

export interface DbTableSummary {
  name: string;
  columns: number;
  rows: number;
}

export interface DbBrowsePage {
  table: string;
  columns: DbColumn[];
  rows: Record<string, unknown>[];
  /** Display names for foreign-key values: `labels[column][id]`. */
  labels: Record<string, Record<string, DbRowLabel>>;
  total: number;
  page: number;
  pageSize: number;
}

export interface DbBrowseQuery {
  q?: string;
  column?: string;
  sort?: string;
  dir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export async function listDbTables(): Promise<DbTableSummary[]> {
  const body = await send<{ tables: DbTableSummary[] }>(() => api.dbbrowser.tables.$get());
  return body.tables;
}

export async function browseDbTable(table: string, query: DbBrowseQuery): Promise<DbBrowsePage> {
  return send<DbBrowsePage>(() =>
    api.dbbrowser.tables[":table"].$get({
      param: { table },
      query: clean(query as Record<string, unknown>) as Record<string, string>,
    }),
  );
}

/**
 * Builds the export URL rather than fetching it, so the browser performs a
 * normal navigation and shows its own download UI. `credentials: "include"`
 * does not apply to a link click, but the session cookie is sent because the
 * request is same-site to the API origin.
 */
export function dbExportUrl(
  table: string,
  query: DbBrowseQuery,
  format: "xlsx" | "csv",
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(clean(query as Record<string, unknown>))) {
    params.set(key, String(value));
  }
  params.set("format", format);
  // pageSize/page are a screen concern; an export always covers the filter.
  params.delete("page");
  params.delete("pageSize");
  return `${API_URL}/dbbrowser/tables/${encodeURIComponent(table)}/export?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// E-Learning (/learning, learners; /learning/admin, admin only)
// ---------------------------------------------------------------------------
type CoursesRes = InferResponseType<typeof api.learning.courses.$get, 200>;
type AdminCoursesRes = InferResponseType<typeof api.learning.admin.courses.$get, 200>;
type AdminSaveRes = InferResponseType<typeof api.learning.admin.courses.$put, 200>;
type YoutubeRes = InferResponseType<typeof api.learning.admin.youtube.$post, 200>;

/** A video group the viewer's role may watch, with every video in file order. */
export type LearningCourse = CoursesRes["courses"][number];
export type Lesson = LearningCourse["lessons"][number];
export type YoutubeInfo = YoutubeRes;

/** A course as the admin edits it; `id` is omitted for a new course or lesson (the server makes it). */
type StoredCourse = AdminCoursesRes["courses"][number];
export type LearningRole = StoredCourse["roles"][number];
export type AdminLesson = Omit<StoredCourse["lessons"][number], "id"> & { id?: string };
export type AdminCourse = Omit<StoredCourse, "id" | "lessons"> & { id?: string; lessons: AdminLesson[] };

export async function listCourses(): Promise<LearningCourse[]> {
  const body = await send<CoursesRes>(() => api.learning.courses.$get());
  return body.courses;
}

export async function listAdminCourses(): Promise<AdminCourse[]> {
  const body = await send<AdminCoursesRes>(() => api.learning.admin.courses.$get());
  return body.courses;
}

/** Saves every course at once; a 422 names the fields in its message. */
export async function saveAdminCourses(courses: AdminCourse[]): Promise<AdminCourse[]> {
  const body = await send<AdminSaveRes>(() => api.learning.admin.courses.$put({ json: { courses } }));
  return body.courses;
}

export async function checkYoutube(url: string): Promise<YoutubeInfo> {
  return send<YoutubeRes>(() => api.learning.admin.youtube.$post({ json: { url } }));
}

// ---------------------------------------------------------------------------
// Pusat Regulasi (/regulations, every signed-in role) and the template
// library (/templates, user-app roles; drafting goes through the chat)
// ---------------------------------------------------------------------------
type RegulationsRes = InferResponseType<typeof api.regulations.$get, 200>;
type TemplatesRes = InferResponseType<typeof api.templates.$get, 200>;
type TemplateDraftRes = InferResponseType<(typeof api.templates)[":id"]["draft"]["$post"], 201>;

export type RegulationCenter = RegulationsRes;
export type Regulation = RegulationsRes["regulations"][number];
export type RegulationAudience = Regulation["audiences"][number];
export type TemplateLibrary = TemplatesRes;
export type TemplateItem = TemplatesRes["templates"][number];
export type TemplateDraft = TemplateDraftRes;

export async function getRegulations(): Promise<RegulationCenter> {
  return send<RegulationsRes>(() => api.regulations.$get());
}

export async function getTemplates(): Promise<TemplateLibrary> {
  return send<TemplatesRes>(() => api.templates.$get());
}

/** Starts the drafting conversation (inside `projectId` when given); answers its id. */
export async function draftTemplate(id: string, projectId?: string): Promise<TemplateDraft> {
  return send<TemplateDraftRes>(() => api.templates[":id"].draft.$post({ param: { id }, json: projectId ? { projectId } : {} }));
}

// The outputs of the PBJ cycle (Non-Penyedia): the dataset nodes with an
// Output/Dokumen, drafted through the chat like a template.
type PbjOutputsRes = InferResponseType<typeof api.templates.outputs.$get, 200>;
type NodeDraftRes = InferResponseType<(typeof api.templates.nodes)[":nodeId"]["draft"]["$post"], 201>;

export type PbjOutputList = PbjOutputsRes;
export type PbjOutput = PbjOutputsRes["outputs"][number];

/** Every stage's outputs, or only those of a PBJ project's route. */
export async function getPbjOutputs(projectId?: string): Promise<PbjOutputList> {
  return send<PbjOutputsRes>(() => api.templates.outputs.$get({ query: projectId ? { projectId } : {} }));
}

/** Drafts a PBJ node's output (inside `projectId` when given); answers the conversation. */
export async function draftNodeOutput(nodeId: string, projectId?: string): Promise<TemplateDraft> {
  return send<NodeDraftRes>(() => api.templates.nodes[":nodeId"].draft.$post({ param: { nodeId }, json: projectId ? { projectId } : {} }));
}

// Unduh .docx (Non-Penyedia): the Word skeleton of a government template or
// a node's output, built at once without Pluto, with the PBJ project's data
// when `projectId` is given. Fetched (not a link) so errors read in place.
export interface DocxFile {
  blob: Blob;
  filename: string;
}

/** The filename of a Content-Disposition header (the UTF-8 form first). */
export function dispositionFilename(header: string | null | undefined, fallback: string): string {
  if (!header) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1]!.trim());
    } catch {
      // Malformed escape: fall through to the plain form.
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header);
  return plain ? plain[1]! : fallback;
}

async function sendDocx(request: () => Promise<ResLike & { blob(): Promise<Blob> }>): Promise<DocxFile> {
  let res: ResLike & { blob(): Promise<Blob> };
  try {
    res = await request();
  } catch {
    throw new ApiError(0, FALLBACK[0]);
  }
  if (!res.ok) return failure(res, true);
  return { blob: await res.blob(), filename: dispositionFilename(res.headers?.get("content-disposition"), "Dokumen.docx") };
}

export async function downloadTemplateDocx(id: string, projectId?: string): Promise<DocxFile> {
  return sendDocx(() => api.templates[":id"].docx.$get({ param: { id }, query: projectId ? { projectId } : {} }));
}

export async function downloadNodeDocx(nodeId: string, projectId?: string): Promise<DocxFile> {
  return sendDocx(() => api.templates.nodes[":nodeId"].docx.$get({ param: { nodeId }, query: projectId ? { projectId } : {} }));
}

// Keluaran dari proses Penyedia: the supplier steps with an output (all of
// them, or a paket project's own), and the Word skeleton of one step.
type SupplierOutputsRes = InferResponseType<(typeof api.templates)["supplier-outputs"]["$get"], 200>;
export type SupplierOutputList = SupplierOutputsRes;
export type SupplierOutput = SupplierOutputsRes["outputs"][number];

export async function getSupplierOutputs(projectId?: string): Promise<SupplierOutputList> {
  return send<SupplierOutputsRes>(() => api.templates["supplier-outputs"].$get({ query: projectId ? { projectId } : {} }));
}

export async function downloadStepDocx(code: string, projectId?: string): Promise<DocxFile> {
  return sendDocx(() => api.templates.steps[":code"].docx.$get({ param: { code }, query: projectId ? { projectId } : {} }));
}

// Documents per activity of a project (/activity-docs, both sides): Pluto
// drafts an activity's document into the right panel, the user saves it as
// the activity's next version or uploads an edited DOCX, and the newest
// saved version of the activity before is the next draft's reference.
const activityDocs = api["activity-docs"];
type ActivityDocListRes = InferResponseType<(typeof activityDocs)[":id"]["$get"], 200>;
type ActivityDocRes = InferResponseType<(typeof activityDocs)[":id"]["documents"][":docId"]["$get"], 200>;

export type ActivityDocState = ActivityDocListRes["items"][number];
export type ActivityDocSummary = NonNullable<ActivityDocState["saved"]>;
export type ActivityDoc = ActivityDocRes["document"];
export interface SkippedActivity {
  key: string;
  title: string;
  stageLabel: string;
}

/** The project's saved versions and the caller's drafts, per activity that has any. */
export async function listActivityDocs(projectId: string): Promise<ActivityDocState[]> {
  return (await send<ActivityDocListRes>(() => activityDocs[":id"].$get({ param: { id: projectId } }))).items;
}

export async function getActivityDoc(projectId: string, docId: string): Promise<ActivityDoc> {
  return (await send<ActivityDocRes>(() => activityDocs[":id"].documents[":docId"].$get({ param: { id: projectId, docId } }))).document;
}

/**
 * Drafts an activity's document. While earlier activities are still open
 * the API drafts nothing and answers 409 with code skipped_activities; the
 * caller asks the user and retries with confirmSkip.
 */
export async function generateActivityDoc(projectId: string, key: string, input: { instructions?: string; confirmSkip?: boolean }): Promise<ActivityDoc> {
  type Res = InferResponseType<(typeof activityDocs)[":id"]["activities"][":key"]["generate"]["$post"], 201>;
  return (await send<Res>(() => activityDocs[":id"].activities[":key"].generate.$post({ param: { id: projectId, key }, json: input }))).document;
}

/** The skipped activities of a 409 skipped_activities answer, or null for any other error. */
export function skippedActivities(err: unknown): { skipped: SkippedActivity[]; total: number } | null {
  if (!(err instanceof ApiError) || err.status !== 409 || err.body?.code !== "skipped_activities") return null;
  const skipped = Array.isArray(err.body.skipped) ? (err.body.skipped as SkippedActivity[]) : [];
  const total = typeof err.body.skippedTotal === "number" ? err.body.skippedTotal : skipped.length;
  return { skipped, total };
}

export async function saveActivityDoc(projectId: string, docId: string): Promise<ActivityDoc> {
  type Res = InferResponseType<(typeof activityDocs)[":id"]["documents"][":docId"]["save"]["$post"], 200>;
  return (await send<Res>(() => activityDocs[":id"].documents[":docId"].save.$post({ param: { id: projectId, docId } }))).document;
}

/** Edit manual one section (the block index of its heading); the result is the user's new draft. */
export async function editActivityDocSection(projectId: string, docId: string, heading: number, content: string): Promise<ActivityDoc> {
  type Res = InferResponseType<(typeof activityDocs)[":id"]["documents"][":docId"]["section"]["$post"], 201>;
  return (await send<Res>(() => activityDocs[":id"].documents[":docId"].section.$post({ param: { id: projectId, docId }, json: { heading, content } }))).document;
}

/** AI ulang bagian ini: Pluto rewrites only that section; the result is the user's new draft. */
export async function rewriteActivityDocSection(projectId: string, docId: string, heading: number, instructions?: string): Promise<ActivityDoc> {
  type Res = InferResponseType<(typeof activityDocs)[":id"]["documents"][":docId"]["section"]["rewrite"]["$post"], 201>;
  return (
    await send<Res>(() =>
      activityDocs[":id"].documents[":docId"].section.rewrite.$post({ param: { id: projectId, docId }, json: { heading, ...(instructions ? { instructions } : {}) } }),
    )
  ).document;
}

/** Unggah hasil edit: a DOCX the user edited becomes the activity's next saved version. */
export async function uploadActivityDoc(projectId: string, key: string, file: File): Promise<ActivityDoc> {
  type Res = InferResponseType<(typeof activityDocs)[":id"]["activities"][":key"]["upload"]["$post"], 201>;
  return (await send<Res>(() => activityDocs[":id"].activities[":key"].upload.$post({ param: { id: projectId, key }, form: { file } }))).document;
}

// Reviu dokumen: Pluto reviews a SAVED version (a draft answers 409); the
// latest review per document is kept and replaced by the next one.
type ActivityDocReviewRes = InferResponseType<(typeof activityDocs)[":id"]["documents"][":docId"]["review"]["$get"], 200>;
export type ActivityDocReview = NonNullable<ActivityDocReviewRes["review"]>;
export type ReviewFinding = ActivityDocReview["findings"][number];
export type ReviewStatus = ActivityDocReview["status"];

/** The document's latest review, or null when it has none. */
export async function getActivityDocReview(projectId: string, docId: string): Promise<ActivityDocReview | null> {
  return (await send<ActivityDocReviewRes>(() => activityDocs[":id"].documents[":docId"].review.$get({ param: { id: projectId, docId } }))).review;
}

/** Reviu dokumen: one Pluto review of the saved version; replaces the earlier one. */
export async function reviewActivityDoc(projectId: string, docId: string): Promise<ActivityDocReview> {
  type Res = InferResponseType<(typeof activityDocs)[":id"]["documents"][":docId"]["review"]["$post"], 201>;
  return (await send<Res>(() => activityDocs[":id"].documents[":docId"].review.$post({ param: { id: projectId, docId } }))).review;
}

// Library (GET /library): the user's own uploads, the files Pluto made from
// a chat answer, and the activity documents they saved, newest first.
type LibraryRes = InferResponseType<typeof api.library.$get, 200>;
export type LibraryItem = LibraryRes["items"][number];

export async function getLibrary(): Promise<LibraryItem[]> {
  return (await send<LibraryRes>(() => api.library.$get())).items;
}

/** The document as a file: the uploaded original, or a DOCX of the draft or version. */
export async function downloadActivityDoc(projectId: string, docId: string): Promise<DocxFile> {
  return sendDocx(() => activityDocs[":id"].documents[":docId"].file.$get({ param: { id: projectId, docId } }));
}

/** A merged file with how many activities it holds, how many have no saved version, and whether a copy went to the Drive. */
export interface MergedDocs extends DocxFile {
  activities: number;
  missing: number;
  stored: boolean;
}

/** Gabungkan dokumen: one stage's saved versions, or the whole project's without `stage`, in one DOCX. */
export async function mergeActivityDocs(projectId: string, stage?: string): Promise<MergedDocs> {
  let headers: { get(name: string): string | null } | undefined;
  const file = await sendDocx(async () => {
    const res = await activityDocs[":id"].merge.$post({ param: { id: projectId }, json: stage ? { stage } : {} });
    headers = res.headers;
    return res;
  });
  return {
    ...file,
    activities: Number(headers?.get("x-merge-activities") ?? 0) || 0,
    missing: Number(headers?.get("x-merge-missing") ?? 0) || 0,
    stored: headers?.get("x-merge-stored") === "1",
  };
}

// Riwayat pembuatan dokumen (Non-Penyedia): the newest drafts and downloads.
type TemplateHistoryRes = InferResponseType<typeof api.templates.history.$get, 200>;
export type TemplateHistoryItem = TemplateHistoryRes["documents"][number];

export async function getTemplateHistory(): Promise<TemplateHistoryItem[]> {
  return (await send<TemplateHistoryRes>(() => api.templates.history.$get())).documents;
}


// ---------------------------------------------------------------------------
// Simulated Google login (TODO(F1): real Google OAuth + payment activation)
// ---------------------------------------------------------------------------
type GoogleDemoRes = InferResponseType<typeof api.auth.google.demo.$post, 200>;

/**
 * POST /auth/google/demo: 404 when disabled, 409 { code: "demo_unavailable" }
 * when the email cannot sign in this way (the API's message says what to do).
 */
export async function googleDemoLogin(email: string, name: string): Promise<GoogleDemoRes> {
  return send<GoogleDemoRes>(() => api.auth.google.demo.$post({ json: { email, name } }), false);
}

// ---------------------------------------------------------------------------
// Onboarding (agreement, then account kind) and the Profil role switch
// ---------------------------------------------------------------------------
type OnboardingRes = InferResponseType<typeof api.onboarding.$get, 200>;
type ChooseRoleRes = InferResponseType<typeof api.onboarding.role.$post, 200>;
type SwitchRoleRes = InferResponseType<typeof api.me.role.$patch, 200>;

export type OnboardingState = OnboardingRes;
export type ChooseRoleInput = InferRequestType<typeof api.onboarding.role.$post>["json"];
export type NonPenyediaRole = InferRequestType<typeof api.me.role.$patch>["json"]["role"];

export async function getOnboarding(): Promise<OnboardingState> {
  return send<OnboardingRes>(() => api.onboarding.$get());
}

/** 422 { code: "agreement_outdated" } when `version` is not the current one. */
export async function acceptAgreement(version: string): Promise<OnboardingState> {
  return send<OnboardingRes>(() => api.onboarding.agreement.$post({ json: { version } }));
}

/** 409 { code: "agreement_required" | "role_already_chosen" }. The session cookie is reissued. */
export async function chooseRole(input: ChooseRoleInput): Promise<User> {
  const body = await send<ChooseRoleRes>(() => api.onboarding.role.$post({ json: input }));
  return body.user;
}

/** Non-Penyedia to Non-Penyedia only; 403 { code: "role_change_forbidden" } otherwise. */
export async function switchRole(role: NonPenyediaRole): Promise<User> {
  const body = await send<SwitchRoleRes>(() => api.me.role.$patch({ json: { role } }));
  return body.user;
}

type PenyediaTypeRes = InferResponseType<(typeof api.me)["penyedia-type"]["$patch"], 200>;
export type PenyediaType = NonNullable<InferRequestType<(typeof api.me)["penyedia-type"]["$patch"]>["json"]["penyediaType"]>;

/** Badan Usaha or Perorangan (Penyedia only; 403 { code: "penyedia_only" } otherwise); null clears it. */
export async function setPenyediaType(penyediaType: PenyediaType | null): Promise<User> {
  const body = await send<PenyediaTypeRes>(() => api.me["penyedia-type"].$patch({ json: { penyediaType } }));
  return body.user;
}

// ---------------------------------------------------------------------------
// Guest chat (signed out, nothing stored)
// ---------------------------------------------------------------------------
type GuestChatRes = InferResponseType<typeof api.chat.guest.$post, 200>;
type GuestFaqRes = InferResponseType<typeof api.chat.guest.faq.$get, 200>;

export type GuestChatInput = InferRequestType<typeof api.chat.guest.$post>["json"];
export type GuestReply = GuestChatRes;
export type GuestHistoryItem = NonNullable<GuestChatInput["history"]>[number];

/** 429 { code: "guest_limit", retryAfter, remaining: 0 } when today's guest quota is used up. */
export async function guestChat(input: GuestChatInput): Promise<GuestReply> {
  return send<GuestChatRes>(() => api.chat.guest.$post({ json: input }), false);
}

export async function guestFaq(): Promise<GuestFaqRes["items"]> {
  const body = await send<GuestFaqRes>(() => api.chat.guest.faq.$get(), false);
  return body.items;
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------
type ProjectsRes = InferResponseType<typeof api.projects.$get, 200>;
type ProjectCreateRes = InferResponseType<typeof api.projects.$post, 201>;
type ProjectPatchRes = InferResponseType<(typeof api.projects)[":id"]["$patch"], 200>;

export type Project = ProjectsRes["projects"][number];

export async function listProjects(): Promise<Project[]> {
  const body = await send<ProjectsRes>(() => api.projects.$get());
  return body.projects;
}

export type ProjectCreateInput = InferRequestType<typeof api.projects.$post>["json"];

/**
 * 409 { code: "project_limit" } at 100 projects. A Penyedia's project is a
 * paket (Ikuti paket baru) with the optional paket fields.
 */
export async function createProject(input: string | ProjectCreateInput): Promise<Project> {
  const json = typeof input === "string" ? { name: input } : input;
  const body = await send<ProjectCreateRes>(() => api.projects.$post({ json }));
  return body.project;
}

export async function renameProject(id: string, name: string): Promise<Project> {
  const body = await send<ProjectPatchRes>(() => api.projects[":id"].$patch({ param: { id }, json: { name } }));
  return body.project;
}

export async function deleteProject(id: string): Promise<void> {
  await send(() => api.projects[":id"].$delete({ param: { id } }));
}

// ---------------------------------------------------------------------------
// Penyedia paket projects and pendampingan (POC)
// ---------------------------------------------------------------------------
type ProjectDetailRes = InferResponseType<(typeof api.projects)[":id"]["$get"], 200>;
type ChecklistPatchRes = InferResponseType<(typeof api.projects)[":id"]["checklist"][":itemId"]["$patch"], 200>;
type ConsultationsRes = InferResponseType<(typeof api.projects)[":id"]["consultations"]["$get"], 200>;
type ConsultantViewRes = InferResponseType<(typeof api.consultations)[":id"]["$get"], 200>;
type ConsultantsRes = InferResponseType<(typeof api.admin.consultations.consultants)["$get"], 200>;

export type ProjectDetail = ProjectDetailRes;
export type ChecklistGroup = ProjectDetail["checklist"][number];
export type ChecklistItem = ChecklistGroup["items"][number];
export type PaketStage = NonNullable<Project["stage"]>;
export type PaketOutcome = NonNullable<Project["outcome"]>;
export type ChecklistStage = ChecklistGroup["stage"];
export type JenisPengadaan = NonNullable<Project["jenisPengadaan"]>;
export type MetodePemilihan = NonNullable<Project["metode"]>;
export type Kualifikasi = NonNullable<Project["kualifikasi"]>;
export type SupplierStepDetail = NonNullable<ChecklistItem["detail"]>;
export type ChecklistPatch = InferRequestType<(typeof api.projects)[":id"]["checklist"][":itemId"]["$patch"]>["json"];
export type StageMove = InferRequestType<(typeof api.projects)[":id"]["stage"]["$post"]>["json"];
export type Consultation = ConsultationsRes["consultations"][number];
export type ConsultationNote = Consultation["notes"][number];
export type ConsultationStatus = Consultation["status"];
export type ConsultantView = ConsultantViewRes;
export type Konsultan = ConsultantsRes["consultants"][number];
export type ConsultationAssign = InferRequestType<(typeof api.admin.consultations)[":id"]["$patch"]>["json"];

/** One owned project with its checklist grouped by stage (empty for a folder project). */
export async function getProject(id: string): Promise<ProjectDetail> {
  return send<ProjectDetailRes>(() => api.projects[":id"].$get({ param: { id } }));
}

export async function updateChecklistItem(projectId: string, itemId: string, patch: ChecklistPatch): Promise<ChecklistItem> {
  const body = await send<ChecklistPatchRes>(() => api.projects[":id"].checklist[":itemId"].$patch({ param: { id: projectId, itemId }, json: patch }));
  return body.item;
}

type PaketSaveRes = InferResponseType<(typeof api.projects)[":id"]["paket"]["$patch"], 200>;
export type PaketSaveInput = InferRequestType<(typeof api.projects)[":id"]["paket"]["$patch"]>["json"];
export type PaketSaveResult = PaketSaveRes;
export type PaketProposal = NonNullable<ChatMessage["paketProposal"]>;

/** Saves the paket info (proposal card or edit dialog) and adds requirements to the Tender checklist. */
export async function updatePaket(projectId: string, input: PaketSaveInput): Promise<PaketSaveResult> {
  return send<PaketSaveRes>(() => api.projects[":id"].paket.$patch({ param: { id: projectId }, json: input }));
}

/** 409 { code: "invalid_transition" | "stage_changed" } when the move is not allowed. */
export async function moveStage(projectId: string, move: StageMove): Promise<ProjectDetail> {
  return send<ProjectDetailRes>(() => api.projects[":id"].stage.$post({ param: { id: projectId }, json: move }));
}

export async function listConsultations(projectId: string): Promise<Consultation[]> {
  const body = await send<ConsultationsRes>(() => api.projects[":id"].consultations.$get({ param: { id: projectId } }));
  return body.consultations;
}

export async function requestConsultation(projectId: string, topic: string, message: string): Promise<Consultation> {
  const body = await send<{ consultation: Consultation }>(() => api.projects[":id"].consultations.$post({ param: { id: projectId }, json: { topic, message } }));
  return body.consultation;
}

export async function addOwnerNote(projectId: string, requestId: string, text: string): Promise<Consultation> {
  const body = await send<{ consultation: Consultation }>(() =>
    api.projects[":id"].consultations[":requestId"].notes.$post({ param: { id: projectId, requestId }, json: { body: text } }),
  );
  return body.consultation;
}

/** Konsultan: the requests assigned to me. */
export async function listAssigned(): Promise<Consultation[]> {
  const body = await send<{ consultations: Consultation[] }>(() => api.consultations.assigned.$get());
  return body.consultations;
}

/** Konsultan: one assigned request with its project read-only. */
export async function getAssigned(id: string): Promise<ConsultantView> {
  return send<ConsultantViewRes>(() => api.consultations[":id"].$get({ param: { id } }));
}

export async function addConsultantNote(id: string, text: string): Promise<Consultation> {
  const body = await send<{ consultation: Consultation }>(() => api.consultations[":id"].notes.$post({ param: { id }, json: { body: text } }));
  return body.consultation;
}

export async function setConsultantStatus(id: string, status: "ditugaskan" | "selesai"): Promise<Consultation> {
  const body = await send<{ consultation: Consultation }>(() => api.consultations[":id"].$patch({ param: { id }, json: { status } }));
  return body.consultation;
}

/** Admin: every request, newest first. */
export async function listAllConsultations(): Promise<Consultation[]> {
  const body = await send<{ consultations: Consultation[] }>(() => api.admin.consultations.$get());
  return body.consultations;
}

export async function listKonsultan(): Promise<Konsultan[]> {
  const body = await send<ConsultantsRes>(() => api.admin.consultations.consultants.$get());
  return body.consultants;
}

export async function assignConsultation(id: string, patch: ConsultationAssign): Promise<Consultation> {
  const body = await send<{ consultation: Consultation }>(() => api.admin.consultations[":id"].$patch({ param: { id }, json: patch }));
  return body.consultation;
}

// ---------------------------------------------------------------------------
// Feature visibility per role
// ---------------------------------------------------------------------------
type FeaturesRes = InferResponseType<typeof api.access.features.$get, 200>;
type RoleFeaturesRes = InferResponseType<typeof api.admin["role-features"]["$get"], 200>;
type RoleFeaturePutRes = InferResponseType<typeof api.admin["role-features"]["$put"], 200>;

export type Feature = FeaturesRes["features"][number];
export type RoleFeatureMatrix = RoleFeaturesRes;
export type RoleFeatureEntry = RoleFeatureMatrix["entries"][number];
export type RoleFeatureInput = InferRequestType<typeof api.admin["role-features"]["$put"]>["json"];

/** Features visible to the signed-in user's role (admin: all). */
export async function getFeatures(): Promise<Feature[]> {
  const body = await send<FeaturesRes>(() => api.access.features.$get());
  return body.features;
}

export async function getRoleFeatures(): Promise<RoleFeatureMatrix> {
  return send<RoleFeaturesRes>(() => api.admin["role-features"].$get());
}

export async function setRoleFeature(input: RoleFeatureInput): Promise<RoleFeatureEntry> {
  const body = await send<RoleFeaturePutRes>(() => api.admin["role-features"].$put({ json: input }));
  return body.entry;
}

// ---------------------------------------------------------------------------
// User Agreements (admin)
// ---------------------------------------------------------------------------
type AgreementsRes = InferResponseType<typeof api.admin.agreements.$get, 200>;

export type AgreementsPage = AgreementsRes;
export type AgreementRow = AgreementsRes["agreements"][number];

export async function listAgreements(page = 1, pageSize = 50): Promise<AgreementsPage> {
  return send<AgreementsRes>(() =>
    api.admin.agreements.$get({ query: { page, pageSize } }),
  );
}

/** Export URL for a normal download navigation (same pattern as dbExportUrl). */
export function agreementsExportUrl(format: "xlsx" | "csv"): string {
  return `${API_URL}/admin/agreements/export?format=${format}`;
}

// ---------------------------------------------------------------------------
// Sumber Pengetahuan (admin): the Google service account that reads the
// folder Basis Pengetahuan, and that folder. The JSON key is write-only: GET
// answers client_email, project_id and the last 6 characters of the key id,
// never the key. PUT key: 400 { error: "Input tidak valid (key: pesan)",
// code: "invalid_key" }, 503 { code: "encryption_unavailable" }, 429 with
// Retry-After. Folders: the folders shared with the service account
// (parent "shared") or inside one, with the breadcrumb; 409 sa_key_missing
// or sa_key_rejected. Choosing a folder reads it at Google first (400
// drive_folder_unreadable / drive_not_a_folder); null clears.
// ---------------------------------------------------------------------------
type KsRoutes = (typeof api.admin)["knowledge-source"];
type KnowledgeSourceRes = InferResponseType<KsRoutes["$get"], 200>;
type KnowledgeSourceKeyRes = InferResponseType<KsRoutes["key"]["$put"], 200>;
type KnowledgeSourceKeyDeleteRes = InferResponseType<KsRoutes["key"]["$delete"], 200>;
type KnowledgeSourceAuditRes = InferResponseType<KsRoutes["audit"]["$get"], 200>;
type KnowledgeFoldersRes = InferResponseType<KsRoutes["folders"]["$get"], 200>;
type KnowledgeFolderRes = InferResponseType<KsRoutes["folder"]["$put"], 200>;
type KnowledgeStatusRes = InferResponseType<KsRoutes["status"]["$get"], 200>;
type KnowledgeTestRes = InferResponseType<KsRoutes["test"]["$post"], 200>;

export type KnowledgeSource = KnowledgeSourceRes;
export type KnowledgeSourceAuditItem = KnowledgeSourceAuditRes["items"][number];
export type KnowledgeSourceField = KnowledgeSourceAuditItem["fieldsChanged"][number];
export type KnowledgeFolderList = KnowledgeFoldersRes;
export type KnowledgeFolderItem = KnowledgeFolderList["folders"][number];
export type KnowledgeSourceStatus = KnowledgeStatusRes;
export type KnowledgeSourceReason = NonNullable<KnowledgeSourceStatus["reason"]>;
export type KnowledgeSourceTestResult = KnowledgeTestRes;

/** Picker id of the top level: the folders shared with the service account. */
export const SHARED_FOLDERS = "shared";

export async function getKnowledgeSource(): Promise<KnowledgeSource> {
  return send<KnowledgeSourceRes>(() => api.admin["knowledge-source"].$get());
}

/** Saves the service account JSON key (the file's text as is). */
export async function saveServiceAccountKey(key: string): Promise<KnowledgeSource> {
  return send<KnowledgeSourceKeyRes>(() => api.admin["knowledge-source"].key.$put({ json: { key } }));
}

export async function removeServiceAccountKey(): Promise<KnowledgeSource> {
  return send<KnowledgeSourceKeyDeleteRes>(() => api.admin["knowledge-source"].key.$delete());
}

export async function listKnowledgeFolders(input: { parent?: string; q?: string; pageToken?: string }): Promise<KnowledgeFolderList> {
  return send<KnowledgeFoldersRes>(() =>
    api.admin["knowledge-source"].folders.$get({ query: { parent: input.parent ?? SHARED_FOLDERS, ...clean({ q: input.q, pageToken: input.pageToken }) } }),
  );
}

export async function chooseKnowledgeFolder(folderId: string | null): Promise<KnowledgeSource> {
  return send<KnowledgeFolderRes>(() => api.admin["knowledge-source"].folder.$put({ json: { folderId } }));
}

export async function getKnowledgeSourceStatus(): Promise<KnowledgeSourceStatus> {
  return send<KnowledgeStatusRes>(() => api.admin["knowledge-source"].status.$get());
}

export async function testKnowledgeSource(): Promise<KnowledgeSourceTestResult> {
  return send<KnowledgeTestRes>(() => api.admin["knowledge-source"].test.$post());
}

export async function listKnowledgeSourceAudit(limit = 50): Promise<KnowledgeSourceAuditItem[]> {
  const body = await send<KnowledgeSourceAuditRes>(() => api.admin["knowledge-source"].audit.$get({ query: { limit: String(limit) } }));
  return body.items;
}

/** The message of a 400 "Input tidak valid (key: pesan)", or the whole message. */
export function serviceAccountKeyError(message: string): string {
  const m = /^Input tidak valid \((?:key|folderId): (.*)\)$/s.exec(message.trim());
  return m ? m[1]! : message;
}

// ---------------------------------------------------------------------------
// Chat attachments, generated files and project files (phase 2).
// Uploads: multipart field "files" (1-5, each max 10 MB; PNG, JPG, WEBP,
// PDF, DOCX, XLSX, TXT checked by content) plus optional "projectId".
// Errors: 400 { code: "unsupported_type" | "too_many_files" | "no_files" },
// 413 { code: "file_too_large" }, 503 { code: "drive_not_connected" },
// 429 with Retry-After, 404 for a project or attachment that is not yours.
// ---------------------------------------------------------------------------
type UploadRes = InferResponseType<typeof api.chat.attachments.$post, 201>;
type CreateFileRes = InferResponseType<(typeof api.chat.conversations)[":id"]["messages"][":messageId"]["files"]["$post"], 201>;
type ProjectFilesRes = InferResponseType<(typeof api.projects)[":id"]["files"]["$get"], 200>;

export type AttachmentRef = UploadRes["attachments"][number];
export type ProjectFile = ProjectFilesRes["files"][number];
export type ProjectFiles = ProjectFilesRes;
export type StructuredDocument = NonNullable<ChatMessage["document"]>;
export type DocumentBlock = StructuredDocument["blocks"][number];
export type GeneratedFileFormat = "docx" | "xlsx";

/** Uploads files to Drive (the project's folder when projectId is given); returns refs for attachmentIds. */
export async function uploadAttachments(files: File[], projectId?: string): Promise<AttachmentRef[]> {
  // An undefined projectId is left out of the multipart body by the client.
  const body = await send<UploadRes>(() => api.chat.attachments.$post({ form: { files, projectId: projectId || undefined } }));
  return body.attachments;
}

/** Download URL of an owned attachment (uploaded or generated), for a normal navigation or link. */
export function attachmentDownloadUrl(id: string): string {
  return `${API_URL}/chat/attachments/${encodeURIComponent(id)}/download`;
}

/**
 * URL of the original Drive document behind a knowledge citation, so a
 * Rujukan can be opened instead of only read.
 *
 * A plain link, not a fetch: the session cookie is SameSite=Lax, which a
 * top-level navigation carries, and the API answers with the real file plus
 * its Content-Disposition. The browser then shows a PDF inline and downloads
 * an Office file, which is what a reader expects from a citation.
 */
export function knowledgeFileUrl(driveId: string): string {
  return `${API_URL}/knowledge/drive/${encodeURIComponent(driveId)}/download?lihat=1`;
}

/**
 * Unduh DOCX/XLSX of an assistant message's document: built on request by
 * the API (no Google Drive needed); fetched so errors read in place.
 */
export async function exportMessageFile(conversationId: string, messageId: string, format: GeneratedFileFormat): Promise<DocxFile> {
  let res: ResLike & { blob(): Promise<Blob> };
  try {
    res = await api.chat.conversations[":id"].messages[":messageId"].export.$post({ param: { id: conversationId, messageId }, json: { format } });
  } catch {
    throw new ApiError(0, FALLBACK[0]);
  }
  if (!res.ok) return failure(res, true);
  return { blob: await res.blob(), filename: dispositionFilename(res.headers?.get("content-disposition"), `Dokumen.${format}`) };
}

/** Builds DOCX or XLSX from the structured document of an assistant message and keeps it in Drive (asked twice: the same file). */
export async function createMessageFile(conversationId: string, messageId: string, format: GeneratedFileFormat): Promise<AttachmentRef> {
  const body = await send<CreateFileRes>(() =>
    api.chat.conversations[":id"].messages[":messageId"].files.$post({ param: { id: conversationId, messageId }, json: { format } }),
  );
  return body.attachment;
}

/** The project's files: its chat attachments plus files found only in its Drive folders, each with its docType, also grouped by type. */
export async function listProjectFiles(projectId: string): Promise<ProjectFiles> {
  return send<ProjectFilesRes>(() => api.projects[":id"].files.$get({ param: { id: projectId } }));
}

// ---------------------------------------------------------------------------
// Penyedia paket documents (B5) and the paket intake (B6).
// Typed upload: multipart "files" (1-5, the chat upload's checks and limits)
// plus "docType". Errors as uploadAttachments, plus 400 { code:
// "invalid_doc_type" } and 409 { code: "not_paket" }.
// Intake: multipart "announcement" (required, max 30 000 characters) plus
// optional "files" (0-5, stored as dokumen_pemilihan). 400 { code:
// "empty_announcement" | "announcement_too_long" | "too_many_files" }, 403
// { code: "paket_penyedia_only" }, 429 (AI budget), 502 (Pluto busy). The
// intake only reads; a 200 with proposal null means Pluto found nothing (the
// files are stored).
// ---------------------------------------------------------------------------
type TypedUploadRes = InferResponseType<(typeof api.projects)[":id"]["files"]["$post"], 201>;
type FileTypeRes = InferResponseType<(typeof api.projects)[":id"]["files"][":fileId"]["$patch"], 200>;
type IntakeRes = InferResponseType<(typeof api.projects)[":id"]["intake"]["$post"], 200>;
type IntakeConfirmRes = InferResponseType<(typeof api.projects)[":id"]["intake"]["confirm"]["$post"], 200>;

export type ProjectFileGroup = ProjectFilesRes["groups"][number];
export type ProjectDocType = ProjectFileGroup["docType"];
export type PaketIntakeResult = IntakeRes;
export type PaketIntakeConfirmResult = IntakeConfirmRes;
/** The editable fields of step 1, sent back (checked or edited) to confirmPaketIntake. */
export type PaketIntakeFields = NonNullable<PaketIntakeResult["fields"]>;

/** Display order and labels of the document types (the API sends the same labels on each group). */
export const PROJECT_DOC_TYPES: readonly ProjectDocType[] = ["dokumen_pemilihan", "spesifikasi_teknis", "boq", "draft_kontrak", "lainnya"];

/** Uploads files into a paket project with one document type; returns the stored files. */
export async function uploadProjectFiles(projectId: string, docType: ProjectDocType, files: File[]): Promise<ProjectFile[]> {
  const body = await send<TypedUploadRes>(() => api.projects[":id"].files.$post({ param: { id: projectId }, form: { files, docType } }));
  return body.files;
}

/** Changes the document type of one project file (by its driveFileId). */
export async function setProjectFileType(projectId: string, driveFileId: string, docType: ProjectDocType): Promise<ProjectFile> {
  const body = await send<FileTypeRes>(() =>
    api.projects[":id"].files[":fileId"].$patch({ param: { id: projectId, fileId: driveFileId }, json: { docType } }),
  );
  return body.file;
}

/**
 * Intake step 1: Pluto reads the pasted LPSE announcement and optional
 * dokumen pemilihan files. Answers what it read (proposal, with the text
 * each value came from), the same as editable paket fields (fields), and
 * the stored files. The paket itself is not changed: show `fields` for the
 * user to check, then call confirmPaketIntake.
 */
export async function intakePaket(projectId: string, announcement: string, files: File[] = []): Promise<PaketIntakeResult> {
  return send<IntakeRes>(() =>
    // An empty files array adds no part to the multipart body.
    api.projects[":id"].intake.$post({ param: { id: projectId }, form: { announcement, files } }),
  );
}

/**
 * Intake step 2: saves the fields the user confirmed (an empty or null value
 * keeps the saved one), adds the requirements to the checklist (up to its
 * cap) and ticks the announcement step. Answers the project, its checklist,
 * requirements added and steps ticked. 400 for an empty or unknown field,
 * 403 { code: "paket_penyedia_only" }.
 */
export async function confirmPaketIntake(projectId: string, fields: PaketIntakeFields): Promise<PaketIntakeConfirmResult> {
  return send<IntakeConfirmRes>(() => api.projects[":id"].intake.confirm.$post({ param: { id: projectId }, json: fields }));
}

/** Conversations of one project (the default list leaves project conversations out). */
export async function listProjectConversations(projectId: string, q?: string): Promise<ConversationSummary[]> {
  const body = await send<ConversationsRes>(() => api.chat.conversations.$get({ query: clean({ q, projectId }) }));
  return body.conversations;
}

// ---------------------------------------------------------------------------
// Login Google (phase 4): the real Google sign-in, the user's Drive
// connection, and the admin's Login Google settings.
// ---------------------------------------------------------------------------
type GoogleConfigRes = InferResponseType<typeof api.auth.google.config.$get, 200>;
type GoogleConnectionRes = InferResponseType<typeof api.auth.google.connection.$get, 200>;
type LoginGoogleRes = InferResponseType<(typeof api.admin)["login-google"]["$get"], 200>;
type LoginGoogleAuditRes = InferResponseType<(typeof api.admin)["login-google"]["audit"]["$get"], 200>;
type LoginGoogleTestRes = InferResponseType<(typeof api.admin)["login-google"]["test"]["$post"], 200>;
export type GoogleConfig = GoogleConfigRes;
export type DriveConnection = GoogleConnectionRes["connection"];
export type LoginGoogleSettings = LoginGoogleRes["settings"];
export type LoginGoogleInput = InferRequestType<(typeof api.admin)["login-google"]["$put"]>["json"];
export type LoginGoogleAuditItem = LoginGoogleAuditRes["items"][number];
export type LoginGoogleTestResult = LoginGoogleTestRes["result"];

export async function getGoogleConfig(): Promise<GoogleConfig> {
  return send<GoogleConfigRes>(() => api.auth.google.config.$get(), false);
}

/** The full-page URL that starts the real Google sign-in (or a Drive reconnect). */
export function googleStartUrl(opts: { next?: string | null; reconnect?: boolean } = {}): string {
  const q = new URLSearchParams();
  if (opts.next) q.set("next", opts.next);
  if (opts.reconnect) q.set("reconnect", "1");
  const qs = q.toString();
  return `${API_URL}/auth/google/start${qs ? `?${qs}` : ""}`;
}

export async function getDriveConnection(): Promise<DriveConnection> {
  const body = await send<GoogleConnectionRes>(() => api.auth.google.connection.$get());
  return body.connection;
}

export async function disconnectDrive(): Promise<DriveConnection> {
  const body = await send<GoogleConnectionRes>(() => api.auth.google.disconnect.$post());
  return body.connection;
}

export async function getLoginGoogle(): Promise<LoginGoogleSettings> {
  const body = await send<LoginGoogleRes>(() => api.admin["login-google"].$get());
  return body.settings;
}

export async function saveLoginGoogle(input: LoginGoogleInput): Promise<LoginGoogleSettings> {
  const body = await send<LoginGoogleRes>(() => api.admin["login-google"].$put({ json: input }));
  return body.settings;
}

export async function listLoginGoogleAudit(limit = 50): Promise<LoginGoogleAuditItem[]> {
  const body = await send<LoginGoogleAuditRes>(() => api.admin["login-google"].audit.$get({ query: { limit } }));
  return body.items;
}

export async function testLoginGoogle(): Promise<LoginGoogleTestResult> {
  const body = await send<LoginGoogleTestRes>(() => api.admin["login-google"].test.$post());
  return body.result;
}

// Penyimpanan Pluto (Drive Pluto, plan 2026-10-21 Fase A): the central Google
// account every new chat upload and generated file is stored in.
type PlutoDriveRes = InferResponseType<(typeof api.admin)["pluto-drive"]["$get"], 200>;
type PlutoDriveStartRes = InferResponseType<(typeof api.admin)["pluto-drive"]["start"]["$post"], 200>;
type PlutoDriveTestRes = InferResponseType<(typeof api.admin)["pluto-drive"]["test"]["$post"], 200>;
export type PlutoDrive = PlutoDriveRes["drive"];
export type PlutoDriveTestResult = PlutoDriveTestRes["result"];

export async function getPlutoDrive(): Promise<PlutoDrive> {
  return (await send<PlutoDriveRes>(() => api.admin["pluto-drive"].$get())).drive;
}

/** Hubungkan: the Google consent URL the page then navigates to (the flow cookie is set by this call). */
export async function startPlutoDrive(): Promise<string> {
  return (await send<PlutoDriveStartRes>(() => api.admin["pluto-drive"].start.$post())).url;
}

export async function testPlutoDrive(): Promise<PlutoDriveTestResult> {
  return (await send<PlutoDriveTestRes>(() => api.admin["pluto-drive"].test.$post())).result;
}

export async function disconnectPlutoDrive(): Promise<PlutoDrive> {
  return (await send<PlutoDriveRes>(() => api.admin["pluto-drive"].disconnect.$post())).drive;
}

// Folder data pengguna: the Drive folder that holds "Users/". Google Picker
// runs as the central account (a short-lived drive.file token from the API);
// picking grants the account access, then the pick is stored.
type PlutoDrivePickerRes = InferResponseType<(typeof api.admin)["pluto-drive"]["picker"]["$get"], 200>;
export type PlutoDrivePicker = PlutoDrivePickerRes["picker"];

export async function getPlutoDrivePicker(): Promise<PlutoDrivePicker> {
  return (await send<PlutoDrivePickerRes>(() => api.admin["pluto-drive"].picker.$get())).picker;
}

/** A picked folder id or a pasted folder link; 422 folder_no_access when the account cannot see it. */
export async function setPlutoDriveUsersFolder(folder: string): Promise<PlutoDrive> {
  return (await send<PlutoDriveRes>(() => api.admin["pluto-drive"].folder.$put({ json: { folder } }))).drive;
}

// ---------------------------------------------------------------------------
// Penyedia menus: Pantau Paket (/watchlist) and Alur Kerja (/pbj)
// ---------------------------------------------------------------------------
type WatchlistRes = InferResponseType<typeof api.watchlist.$get, 200>;
type WatchCreateRes = InferResponseType<typeof api.watchlist.$post, 201>;
type WatchPatchRes = InferResponseType<(typeof api.watchlist)[":id"]["$patch"], 200>;
type WatchProjectRes = InferResponseType<(typeof api.watchlist)[":id"]["project"]["$post"], 201>;
type SupplierGroupsRes = InferResponseType<typeof api.pbj.supplier.groups.$get, 200>;
type SupplierStepsRes = InferResponseType<typeof api.pbj.supplier.steps.$get, 200>;
type SupplierStepRes = InferResponseType<(typeof api.pbj.supplier.steps)[":code"]["$get"], 200>;
type SupplierReferencesRes = InferResponseType<typeof api.pbj.supplier.references.$get, 200>;
type SupplierRulesRes = InferResponseType<typeof api.pbj.supplier.rules.$get, 200>;
type PbjNodesRes = InferResponseType<typeof api.pbj.nodes.$get, 200>;
type PbjDiagramsRes = InferResponseType<typeof api.pbj.diagrams.$get, 200>;
type PbjRegulationsRes = InferResponseType<typeof api.pbj.regulations.$get, 200>;

export type WatchItem = WatchlistRes["items"][number];
export type WatchStatus = WatchItem["status"];
export type WatchQuery = InferRequestType<typeof api.watchlist.$get>["query"];
export type WatchInput = InferRequestType<typeof api.watchlist.$post>["json"];
export type WatchPatch = InferRequestType<(typeof api.watchlist)[":id"]["$patch"]>["json"];
export type WatchProjectResult = WatchProjectRes;
export type SupplierGroup = SupplierGroupsRes["groups"][number];
export type SupplierStep = SupplierStepsRes["steps"][number];
export type SupplierReference = SupplierReferencesRes["references"][number];
export type SupplierRule = SupplierRulesRes["rules"][number];
export type PbjNode = PbjNodesRes["nodes"][number];
export type PbjDiagram = PbjDiagramsRes["diagrams"][number];
export type PbjRegulation = PbjRegulationsRes["regulations"][number];

export async function listWatchlist(query: WatchQuery = {}): Promise<WatchItem[]> {
  const body = await send<WatchlistRes>(() => api.watchlist.$get({ query: clean(query) }));
  return body.items;
}

/** 409 { code: "watchlist_limit" } at 500 records. */
export async function createWatchItem(input: WatchInput): Promise<WatchItem> {
  const body = await send<WatchCreateRes>(() => api.watchlist.$post({ json: input }));
  return body.item;
}

export async function updateWatchItem(id: string, patch: WatchPatch): Promise<WatchItem> {
  const body = await send<WatchPatchRes>(() => api.watchlist[":id"].$patch({ param: { id }, json: patch }));
  return body.item;
}

export async function deleteWatchItem(id: string): Promise<void> {
  await send(() => api.watchlist[":id"].$delete({ param: { id } }));
}

/** Jadikan project: a paket project from the record; 409 { code: "already_project" | "project_limit" }. */
export async function watchItemToProject(id: string): Promise<WatchProjectResult> {
  return send<WatchProjectRes>(() => api.watchlist[":id"].project.$post({ param: { id } }));
}

export async function listSupplierGroups(): Promise<SupplierGroup[]> {
  const body = await send<SupplierGroupsRes>(() => api.pbj.supplier.groups.$get());
  return body.groups;
}

export async function listSupplierSteps(query: { group?: string; q?: string } = {}): Promise<SupplierStep[]> {
  const body = await send<SupplierStepsRes>(() => api.pbj.supplier.steps.$get({ query: clean(query) }));
  return body.steps;
}

export async function getSupplierStep(code: string): Promise<SupplierStep> {
  const body = await send<SupplierStepRes>(() => api.pbj.supplier.steps[":code"].$get({ param: { code } }));
  return body.step;
}

export async function listSupplierRules(): Promise<SupplierRule[]> {
  const body = await send<SupplierRulesRes>(() => api.pbj.supplier.rules.$get());
  return body.rules;
}

export async function listSupplierReferences(): Promise<SupplierReference[]> {
  const body = await send<SupplierReferencesRes>(() => api.pbj.supplier.references.$get());
  return body.references;
}

export async function listPbjNodes(query: { diagram?: string; q?: string } = {}): Promise<PbjNode[]> {
  const body = await send<PbjNodesRes>(() => api.pbj.nodes.$get({ query: clean(query) }));
  return body.nodes;
}

export async function listPbjDiagrams(): Promise<PbjDiagram[]> {
  const body = await send<PbjDiagramsRes>(() => api.pbj.diagrams.$get());
  return body.diagrams;
}

export async function listPbjRegulations(): Promise<PbjRegulation[]> {
  const body = await send<PbjRegulationsRes>(() => api.pbj.regulations.$get());
  return body.regulations;
}

// ---------------------------------------------------------------------------
// PBJ, Non-Penyedia (migration 0023): Alur PBJ, PBJ projects, Dashboard PBJ
// ---------------------------------------------------------------------------
type PbjFlowRes = InferResponseType<typeof api.pbj.flow.$get, 200>;
type PbjDiagramRes = InferResponseType<(typeof api.pbj.flow)[":code"]["$get"], 200>;
type PbjProjectsRes = InferResponseType<typeof api.pbj.projects.$get, 200>;
type PbjDetailRes = InferResponseType<(typeof api.pbj.projects)[":id"]["$get"], 200>;
type PbjTickRes = InferResponseType<(typeof api.pbj.projects)[":id"]["activities"][":nodeId"]["$put"], 200>;
type PbjDashboardRes = InferResponseType<typeof api.pbj.dashboard.$get, 200>;

export type PbjFlow = PbjFlowRes;
export type PbjDiagramSummary = PbjFlowRes["diagrams"][number];
export type PbjDiagramDetail = PbjDiagramRes;
export type PbjFlowNode = PbjDiagramRes["nodes"][number];
export type PbjProject = PbjProjectsRes["projects"][number];
export type PbjProjectDetail = PbjDetailRes;
export type PbjStageActivities = PbjProjectDetail["stages"][number];
export type PbjActivity = PbjStageActivities["activities"][number];
export type PbjStage = PbjProject["stage"];
export type PbjRoute = PbjProject["route"];
export type PbjMethod = NonNullable<PbjProject["method"]>;
export type SwakelolaType = NonNullable<PbjProject["swakelolaType"]>;
export type PbjDashboard = PbjDashboardRes;
export type PbjCreateInput = InferRequestType<typeof api.pbj.projects.$post>["json"];
export type PbjUpdateInput = InferRequestType<(typeof api.pbj.projects)[":id"]["$patch"]>["json"];

export async function getPbjFlow(): Promise<PbjFlow> {
  return send<PbjFlowRes>(() => api.pbj.flow.$get());
}

export async function getPbjDiagram(code: string): Promise<PbjDiagramDetail> {
  return send<PbjDiagramRes>(() => api.pbj.flow[":code"].$get({ param: { code } }));
}

export async function listPbjProjects(): Promise<PbjProject[]> {
  const body = await send<PbjProjectsRes>(() => api.pbj.projects.$get());
  return body.projects;
}

/** 400 { code: "method_required" | "swakelola_type_required" }; 409 { code: "project_limit" } at 100 projects. */
export async function createPbjProject(input: PbjCreateInput): Promise<PbjProjectDetail> {
  return send<PbjDetailRes>(() => api.pbj.projects.$post({ json: input }));
}

export async function getPbjProject(id: string): Promise<PbjProjectDetail> {
  return send<PbjDetailRes>(() => api.pbj.projects[":id"].$get({ param: { id } }));
}

export async function updatePbjProject(id: string, input: PbjUpdateInput): Promise<PbjProjectDetail> {
  return send<PbjDetailRes>(() => api.pbj.projects[":id"].$patch({ param: { id }, json: input }));
}

/** 403 { code: "not_actor" } when the current role is not the activity's actor. */
export async function tickPbjActivity(id: string, nodeId: string, done: boolean): Promise<PbjActivity> {
  const body = await send<PbjTickRes>(() => api.pbj.projects[":id"].activities[":nodeId"].$put({ param: { id, nodeId }, json: { done } }));
  return body.activity;
}

type PbjExportRes = InferResponseType<(typeof api.pbj.projects)[":id"]["export"]["$get"], 200>;
export type PbjExport = PbjExportRes;

// The RKA (Rencana Kerja dan Anggaran) of a PBJ project (/pbj/projects/:id/rka,
// migration 0038): one per project, a PDF, DOCX or XLSX of up to 10 MB. Its
// text is read by the activity documents of stage 0 and 1.
const pbjRka = api.pbj.projects[":id"].rka;
type PbjRkaRes = InferResponseType<(typeof pbjRka)["$get"], 200>;
type PbjRkaUploadRes = InferResponseType<(typeof pbjRka)["$put"], 200>;
export type PbjRka = NonNullable<PbjRkaRes["rka"]>;

/** The project's RKA, or null when none was uploaded. */
export async function getPbjRka(id: string): Promise<PbjRka | null> {
  return (await send<PbjRkaRes>(() => pbjRka.$get({ param: { id } }))).rka;
}

/**
 * Uploads the RKA, replacing the one the project has (replaced: true).
 * 413 file_too_large; 422 unsupported_type, or rka_unreadable when neither
 * the file could be stored nor its text read. textError on the answer: kept,
 * but the AI cannot read it (e.g. a scanned PDF).
 */
export async function uploadPbjRka(id: string, file: File): Promise<PbjRkaUploadRes> {
  return send<PbjRkaUploadRes>(() => pbjRka.$put({ param: { id }, form: { file } }));
}

/** 404 when the project has no RKA. */
export async function deletePbjRka(id: string): Promise<void> {
  await send<{ ok: true }>(() => pbjRka.$delete({ param: { id } }));
}

/** The RKA's original file (or, when only its text was kept, a Word file of the text). */
export async function downloadPbjRka(id: string): Promise<DocxFile> {
  return sendDocx(() => pbjRka.file.$get({ param: { id } }));
}

// Members and Kirim dokumen tahap 0 of a PBJ project (migration 0037): the
// PA/KPA sends the saved stage-0 documents to a PPK by email; the PPK then
// opens the project as a member (PbjProject.access "member").
const pbjOne = api.pbj.projects[":id"];
type PbjMembersRes = InferResponseType<(typeof pbjOne)["members"]["$get"], 200>;
type PbjMemberRemoveRes = InferResponseType<(typeof pbjOne)["members"][":userId"]["$delete"], 200>;
type PbjHandoversRes = InferResponseType<(typeof pbjOne)["handovers"]["$get"], 200>;
type PbjHandoverRes = InferResponseType<(typeof pbjOne)["handovers"]["$post"], 201>;
export type PbjMembers = PbjMembersRes;
export type PbjHandover = PbjHandoversRes["handovers"][number];

/** The owner and the members of a PBJ project; canManage when the viewer is the owner. */
export async function getPbjMembers(id: string): Promise<PbjMembers> {
  return send<PbjMembersRes>(() => pbjOne.members.$get({ param: { id } }));
}

/**
 * The owner removes a member, or a member leaves (their own userId).
 * 403 owner_only otherwise. Answers the members left, or null after leaving.
 */
export async function removePbjMember(id: string, userId: string): Promise<PbjMembers | null> {
  return (await send<PbjMemberRemoveRes>(() => pbjOne.members[":userId"].$delete({ param: { id, userId } }))).members;
}

/** Every "Kirim dokumen tahap" of the project, newest first. */
export async function listPbjHandovers(id: string): Promise<PbjHandover[]> {
  return (await send<PbjHandoversRes>(() => pbjOne.handovers.$get({ param: { id } }))).handovers;
}

/**
 * Kirim dokumen tahap 0 to a PPK by email. 403 handover_pa_kpa_only (not in
 * PA/KPA mode), 404 recipient_not_found, 400 recipient_is_self, 409
 * no_stage_documents (no saved stage-0 document yet).
 */
export async function sendPbjHandover(id: string, input: { email: string; note?: string | null }): Promise<PbjHandover> {
  return (await send<PbjHandoverRes>(() => pbjOne.handovers.$post({ param: { id }, json: input }))).handover;
}

// Cari Paket (GET /search/projects): the user's own projects of both sides.
type ProjectSearchRes = InferResponseType<typeof api.search.projects.$get, 200>;
export type ProjectSearch = ProjectSearchRes;
export type ProjectSearchResult = ProjectSearchRes["results"][number];

/**
 * Searches the user's projects by name and paket fields (case-insensitive).
 * A query under 2 characters answers { tooShort: true, results: [] }; each
 * result carries href, the path that opens it.
 */
export async function searchProjects(q: string, limit?: number): Promise<ProjectSearch> {
  return send<ProjectSearchRes>(() => api.search.projects.$get({ query: clean({ q, limit }) }));
}

/** Backup project: the paket and its ticked activities (the caller saves it as a .json file). */
export async function exportPbjProject(id: string): Promise<PbjExport> {
  return send<PbjExportRes>(() => api.pbj.projects[":id"].export.$get({ param: { id } }));
}

export async function getPbjDashboard(): Promise<PbjDashboard> {
  return send<PbjDashboardRes>(() => api.pbj.dashboard.$get());
}

// ---------------------------------------------------------------------------
// Data Perusahaan and company groups (/company, feature company_data;
// /admin/company-groups for the admin). A group is one company with members
// (an account is in at most one group); its owner or admins invite by email.
// Company documents (kind "persyaratan" or "umum") belong to the group, or to
// the uploader alone (shared=false, or no group). Pluto reads them in the chat
// and in document drafts of exactly those accounts.
// ---------------------------------------------------------------------------
const company = api.company;
const adminCompany = api.admin["company-groups"];
type CompanyRes = InferResponseType<typeof company.$get, 200>;
type CompanyGroupCreateRes = InferResponseType<typeof company.group.$post, 201>;
type CompanyInviteRes = InferResponseType<typeof company.group.invites.$post, 201>;
type MyCompanyInvitesRes = InferResponseType<typeof company.invites.$get, 200>;
type CompanyDocumentsRes = InferResponseType<typeof company.documents.$get, 200>;
type CompanyDocumentUploadRes = InferResponseType<typeof company.documents.$post, 201>;
type AdminCompanyGroupsRes = InferResponseType<typeof adminCompany.$get, 200>;
type AdminCompanyGroupRes = InferResponseType<(typeof adminCompany)[":id"]["$get"], 200>;

export type CompanyGroup = NonNullable<CompanyRes["group"]>;
export type CompanyGroupRole = CompanyGroup["myRole"];
export type CompanyMember = CompanyGroup["members"][number];
export type CompanyGroupInvite = CompanyInviteRes["invite"];
export type MyCompanyInvite = MyCompanyInvitesRes["invites"][number];
export type CompanyDocument = CompanyDocumentsRes["documents"][number];
export type CompanyDocKind = CompanyDocument["kind"];
export type CompanyDocType = NonNullable<CompanyDocument["docType"]>;
export type CompanyDocumentPatch = InferRequestType<(typeof company.documents)[":docId"]["$patch"]>["json"];
export type AdminCompanyGroupSummary = AdminCompanyGroupsRes["groups"][number];
export type AdminCompanyGroupDetail = AdminCompanyGroupRes;

/** The caller's group, or null when the account is in none. */
export async function getMyCompanyGroup(): Promise<CompanyGroup | null> {
  return (await send<CompanyRes>(() => company.$get())).group;
}

/** 409 { code: "already_in_group" } when the account is already in a group. */
export async function createCompanyGroup(name: string): Promise<CompanyGroup> {
  return (await send<CompanyGroupCreateRes>(() => company.group.$post({ json: { name } }))).group;
}

export async function renameCompanyGroup(name: string): Promise<CompanyGroup> {
  type Res = InferResponseType<typeof company.group.$patch, 200>;
  return (await send<Res>(() => company.group.$patch({ json: { name } }))).group;
}

/** Owner only: the group with its members, invitations and documents. */
export async function deleteCompanyGroup(): Promise<void> {
  await send(() => company.group.$delete());
}

/** 409 { code: "owner_cannot_leave" } for the owner (hand ownership over first, or delete). */
export async function leaveCompanyGroup(): Promise<void> {
  await send(() => company.group.leave.$post());
}

export async function inviteToCompanyGroup(email: string): Promise<CompanyGroupInvite> {
  return (await send<CompanyInviteRes>(() => company.group.invites.$post({ json: { email } }))).invite;
}

export async function revokeCompanyInvite(inviteId: string): Promise<void> {
  await send(() => company.group.invites[":inviteId"].$delete({ param: { inviteId } }));
}

export async function removeCompanyMember(userId: string): Promise<void> {
  await send(() => company.group.members[":userId"].$delete({ param: { userId } }));
}

/** Owner only; role "owner" hands the ownership over (the caller becomes an admin). */
export async function setCompanyMemberRole(userId: string, role: CompanyGroupRole): Promise<CompanyGroup> {
  type Res = InferResponseType<(typeof company.group.members)[":userId"]["$patch"], 200>;
  return (await send<Res>(() => company.group.members[":userId"].$patch({ param: { userId }, json: { role } }))).group;
}

/** Open invitations addressed to the caller's own email. */
export async function getMyCompanyInvites(): Promise<MyCompanyInvite[]> {
  return (await send<MyCompanyInvitesRes>(() => company.invites.$get())).invites;
}

export async function acceptCompanyInvite(inviteId: string): Promise<CompanyGroup> {
  type Res = InferResponseType<(typeof company.invites)[":inviteId"]["accept"]["$post"], 200>;
  return (await send<Res>(() => company.invites[":inviteId"].accept.$post({ param: { inviteId } }))).group;
}

export async function declineCompanyInvite(inviteId: string): Promise<void> {
  await send(() => company.invites[":inviteId"].decline.$post({ param: { inviteId } }));
}

/** The caller's own documents and its group's, newest first. */
export async function getCompanyDocuments(): Promise<CompanyDocument[]> {
  return (await send<CompanyDocumentsRes>(() => company.documents.$get())).documents;
}

export interface CompanyDocumentUpload {
  file: File;
  kind: CompanyDocKind;
  docType?: CompanyDocType | null;
  title?: string;
  /** Default true: into the group when the account is in one; false keeps it to the uploader. */
  shared?: boolean;
}

/** Word, PDF, Excel or .txt, at most 10 MB. indexStatus "failed": saved, not yet readable by Pluto (retry with reindex). */
export async function uploadCompanyDocument(input: CompanyDocumentUpload): Promise<CompanyDocument> {
  const form: Record<string, string | File> = { file: input.file, kind: input.kind };
  if (input.docType) form.docType = input.docType;
  if (input.title) form.title = input.title;
  if (input.shared !== undefined) form.shared = input.shared ? "true" : "false";
  return (await send<CompanyDocumentUploadRes>(() => company.documents.$post({ form: form as never }))).document;
}

export async function updateCompanyDocument(docId: string, patch: CompanyDocumentPatch): Promise<CompanyDocument> {
  type Res = InferResponseType<(typeof company.documents)[":docId"]["$patch"], 200>;
  return (await send<Res>(() => company.documents[":docId"].$patch({ param: { docId }, json: patch }))).document;
}

export async function reindexCompanyDocument(docId: string): Promise<CompanyDocument> {
  type Res = InferResponseType<(typeof company.documents)[":docId"]["reindex"]["$post"], 200>;
  return (await send<Res>(() => company.documents[":docId"].reindex.$post({ param: { docId } }))).document;
}

export async function deleteCompanyDocument(docId: string): Promise<void> {
  await send(() => company.documents[":docId"].$delete({ param: { docId } }));
}

/** The original file (or its extracted text when the original is not in Drive). */
export async function downloadCompanyDocument(docId: string): Promise<DocxFile> {
  return sendDocx(() => company.documents[":docId"].file.$get({ param: { docId } }));
}

// Admin: every group, and one group's members, invitations and document metadata.
export async function getAdminCompanyGroups(): Promise<AdminCompanyGroupSummary[]> {
  return (await send<AdminCompanyGroupsRes>(() => adminCompany.$get())).groups;
}

export async function getAdminCompanyGroup(id: string): Promise<AdminCompanyGroupDetail> {
  return send<AdminCompanyGroupRes>(() => adminCompany[":id"].$get({ param: { id } }));
}

export async function deleteAdminCompanyGroup(id: string): Promise<void> {
  await send(() => adminCompany[":id"].$delete({ param: { id } }));
}

/** 409 { code: "cannot_remove_owner" }: hand ownership over first. */
export async function removeAdminCompanyMember(id: string, userId: string): Promise<void> {
  await send(() => adminCompany[":id"].members[":userId"].$delete({ param: { id, userId } }));
}

export async function setAdminCompanyMemberRole(id: string, userId: string, role: CompanyGroupRole): Promise<AdminCompanyGroupDetail> {
  return send<AdminCompanyGroupRes>(() => adminCompany[":id"].members[":userId"].$patch({ param: { id, userId }, json: { role } }));
}

// ---------------------------------------------------------------------------
// Review Dokumen (/document-review, feature document_review, migration 0044):
// an uploaded file (Word, PDF, Excel, text; 10 MB) or pasted text, its type,
// optionally one of the user's projects and instructions; Pluto answers a
// status, a summary and the findings. Every review is its owner's only.
// ---------------------------------------------------------------------------
const docReview = api["document-review"];
type DocReviewListRes = InferResponseType<typeof docReview.$get, 200>;
type DocReviewCreateRes = InferResponseType<typeof docReview.$post, 201>;

export type DocReviewSummary = DocReviewListRes["reviews"][number];
export type DocReview = DocReviewCreateRes["review"];
export type DocReviewType = DocReview["docType"];

export interface DocReviewInput {
  docType: DocReviewType;
  /** One of the two: the file, or the pasted text. */
  file?: File | null;
  text?: string;
  title?: string;
  projectId?: string | null;
  instructions?: string;
}

/** The caller's reviews, newest first. */
export async function listDocReviews(): Promise<DocReviewSummary[]> {
  return (await send<DocReviewListRes>(() => docReview.$get())).reviews;
}

export async function getDocReview(id: string): Promise<DocReview> {
  type Res = InferResponseType<(typeof docReview)[":id"]["$get"], 200>;
  return (await send<Res>(() => docReview[":id"].$get({ param: { id } }))).review;
}

/** 422 for an unreadable file or a too short text, 404 for a project that is not the caller's, 429 at the hourly limit. */
export async function createDocReview(input: DocReviewInput): Promise<DocReview> {
  const form: Record<string, string | File> = { docType: input.docType };
  if (input.file) form.file = input.file;
  else if (input.text) form.text = input.text;
  if (input.title) form.title = input.title;
  if (input.projectId) form.projectId = input.projectId;
  if (input.instructions) form.instructions = input.instructions;
  return (await send<DocReviewCreateRes>(() => docReview.$post({ form: form as never }))).review;
}

export async function deleteDocReview(id: string): Promise<void> {
  await send(() => docReview[":id"].$delete({ param: { id } }));
}

/** Unduh hasil reviu: the result as Word. */
export async function downloadDocReview(id: string): Promise<DocxFile> {
  return sendDocx(() => docReview[":id"].docx.$get({ param: { id } }));
}
